import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { v4 as uuid } from 'uuid';

export interface ModalConfig {
  dismissButton?: ButtonConfig;
  closeButton?: ButtonConfig;
  xCloseButton?: boolean;
  closeOnBgClick?: boolean;
  showFooter?: boolean;
  customFooter?: boolean;
  headerClass?: string[];
  bodyClass?: string[];
  windowClass?: string;
  closeButtonDisabled?: boolean;
  showDismissButton?: boolean;
  hideCloseButton?: boolean;
  options?: NgbModalOptions;
  titleText?: string;
  modalLabel?: string;
  openOnLoad?: boolean;
  persistAcrossPages?: boolean;
}

interface InternalModalConfig extends ModalConfig {
  closeButton: ButtonConfig;
  headerClass: string[];
  bodyClass?: string[];
  titleText: string;
  backdrop: boolean | 'static';
}

export interface ButtonConfig {
  label?: string;
  classes?: string[];
}

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  standalone: false,
})
export class ModalComponent implements OnInit, AfterViewInit {
  configLoaded = false;

  @Input() public modalConfig: ModalConfig = {};

  @Input() public variableTitle: string;

  @Output() public readonly modalRef: EventEmitter<NgbModalRef> =
    new EventEmitter();

  @Output() public readonly isOpening: EventEmitter<NgbModalRef> =
    new EventEmitter();

  @Output() public readonly isClosing: EventEmitter<NgbModalRef> =
    new EventEmitter();

  @Output() public readonly isDismissing: EventEmitter<NgbModalRef> =
    new EventEmitter();

  @ViewChild('modal')
  public modalContent: TemplateRef<ModalComponent>;

  public modalReference: NgbModalRef;

  modalLabel: string;

  isOpen = false;

  modalLabelId = uuid();

  constructor(private modalService: NgbModal, private router: Router) {}

  ngOnInit() {
    this.loadConfig();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd && !this.config.persistAcrossPages) {
        this.dismiss();
      }
    });
  }

  public config: InternalModalConfig;

  @Input() disableSubmit = false;

  defaultConfig: InternalModalConfig = {
    closeButton: {
      classes: ['btn', 'btn-primary', 'btn-md'],
      label: 'OK',
    },
    closeOnBgClick: true,
    backdrop: true,
    closeButtonDisabled: false,
    headerClass: ['modal-header'],
    bodyClass: ['modal-body'],
    showFooter: true,
    customFooter: false,
    hideCloseButton: false,
    showDismissButton: false,
    options: {},
    titleText: '',
    modalLabel: '',
    openOnLoad: false,
    persistAcrossPages: false,
  };

  ngAfterViewInit() {
    if (this.modalConfig.openOnLoad) {
      this.open();
    }
  }

  loadConfig() {
    this.config = {
      ...this.defaultConfig,
      ...this.modalConfig,
    };
    if (this.modalConfig.headerClass) {
      this.defaultConfig.headerClass = [
        ...this.defaultConfig.headerClass,
        ...(this.modalConfig as InternalModalConfig).headerClass,
      ];
    }
    if (this.config.closeButton.classes?.length === 0) {
      this.config.closeButton.classes = this.defaultConfig.closeButton.classes;
    }
    this.config.options = {
      ...this.config.options,
      ariaLabelledBy: this.modalLabelId,
    };
    if (this.modalConfig.windowClass) {
      this.config.options.windowClass = this.modalConfig.windowClass;
    }
    if (!this.config.closeOnBgClick) {
      this.config.options.backdrop = 'static';
      this.config.options.keyboard = false;
    }
    this.modalLabel =
      this.config.modalLabel || this.config.titleText || 'modal';
    this.configLoaded = true;
  }

  open() {
    this.isOpen = true;
    this.modalReference = this.modalService.open(
      this.modalContent,
      this.config.options
    );
    this.modalRef.emit(this.modalReference);
    this.isOpening.emit(this.modalReference);
    this.modalReference.closed.subscribe(() =>
      this.isClosing.emit(this.modalReference)
    );
    this.modalReference.dismissed.subscribe(() =>
      this.isDismissing.emit(this.modalReference)
    );
  }

  close(): boolean {
    if (this.modalReference) {
      this.modalReference.close();
      this.isOpen = false;
      return true;
    }
    return false;
  }

  dismiss() {
    if (this.modalReference) {
      this.modalReference.dismiss();
    }
  }
}
